import React, { useEffect, useState } from 'react';
import {
    Typography,
    Paper,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';
import API_URL from '../../settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/* ================== Priority Logic ================== */

const getPriorityScore = (patient) => {
    let score = 0;

    // 🟠 Economic
    if (patient.economy?.status === 'ไม่มี') score += 3;
    else if (patient.economy?.status === 'รายได้น้อย') score += 2;

    // 🔴 ADL Risk (ใช้ summary จริง)
    const adlGroup = patient.adl_summary?.latest_group;
    if (adlGroup === 'Bedridden') score += 3;
    else if (adlGroup === 'Homebound') score += 2;

    // 🟡 Welfare
    if (patient.economy?.welfare === false) score += 1;

    return score;
};

const getPriorityChipColor = (score) => {
    if (score >= 6) return 'error';     // เร่งด่วนมาก
    if (score >= 4) return 'warning';   // เร่งด่วน
    return 'info';                      // เฝ้าระวัง
};

const getAdlLabel = (group) => {
    if (group === 'Bedridden') return 'ติดเตียง';
    if (group === 'Homebound') return 'ติดบ้าน';
    if (group === 'Independent') return 'ช่วยเหลือตนเองได้';
    return '-';
};

/* ================== Component ================== */

function AssistancePriority() {
    const { user } = useAuth();
    const [priorityList, setPriorityList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openInfo, setOpenInfo] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const url =
                    user.role === 'VOLUNTEER'
                        ? `${API_URL}/patients?volunteer_id=${user.id}`
                        : `${API_URL}/patients?hospital_id=${user.hospital_id}`;

                const res = await fetch(url);
                const data = await res.json();

                const scored = data
                    .map(p => ({
                        ...p,
                        priorityScore: getPriorityScore(p)
                    }))
                    .filter(p => p.priorityScore > 0)
                    .sort((a, b) => b.priorityScore - a.priorityScore);

                setPriorityList(scored);
            } catch (err) {
                console.error('Failed to fetch priority patients:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleNavigate = (location) => {
        if (location?.lat && location?.lng) {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`,
                '_blank',
                'noopener,noreferrer'
            );
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    จัดลำดับความเร่งด่วนในการช่วยเหลือ
                </Typography>

                <IconButton
                    size="small"
                    onClick={() => setOpenInfo(true)}
                >
                    <InfoOutlinedIcon />
                </IconButton>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ลำดับ</TableCell>
                            <TableCell>ชื่อ-สกุล</TableCell>
                            <TableCell align="center">คะแนน</TableCell>
                            <TableCell>สถานะรายได้</TableCell>
                            <TableCell>ระดับ ADL</TableCell>
                            <TableCell>สวัสดิการ</TableCell>
                            <TableCell align="center">นำทาง</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {priorityList.length > 0 ? (
                            priorityList.map((p, index) => (
                                <TableRow key={p.id}>
                                    <TableCell>{index + 1}</TableCell>

                                    <TableCell>
                                        {p.first_name} {p.last_name}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip
                                            label={p.priorityScore}
                                            color={getPriorityChipColor(p.priorityScore)}
                                            size="small"
                                        />
                                    </TableCell>

                                    <TableCell>{p.economy?.status || '-'}</TableCell>

                                    <TableCell>
                                        {getAdlLabel(p.adl_summary?.latest_group)}
                                    </TableCell>

                                    <TableCell>
                                        {p.economy?.welfare ? 'ได้รับ' : 'ไม่ได้รับ'}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Tooltip title="นำทางไปยังที่อยู่">
                                            <span>
                                                <IconButton
                                                    color="primary"
                                                    disabled={!p.address_detail?.lat || !p.address_detail?.lng}
                                                    onClick={() => handleNavigate(p.address_detail)}
                                                >
                                                    <NavigationIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    ไม่พบข้อมูลผู้ที่ต้องการความช่วยเหลือเร่งด่วน
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={openInfo} onClose={() => setOpenInfo(false)}>
                <DialogTitle>เกณฑ์การคำนวณคะแนนความเร่งด่วน</DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" gutterBottom>
                        ระบบใช้ข้อมูลด้านเศรษฐกิจและความสามารถในการช่วยเหลือตนเอง (ADL)
                        เพื่อจัดลำดับความเร่งด่วนในการช่วยเหลือ ดังนี้
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">ด้านเศรษฐกิจ</Typography>
                        <ul>
                            <li>ไม่มี <strong>+3 คะแนน</strong></li>
                            <li>รายได้น้อย <strong>+2 คะแนน</strong></li>
                            <li>ไม่ได้รับสวัสดิการจากรัฐ <strong>+1 คะแนน</strong></li>
                        </ul>

                        <Typography variant="subtitle2">ด้านสุขภาพ (ADL)</Typography>
                        <ul>
                            <li>ผู้ป่วยติดเตียง (Bedridden) <strong>+3 คะแนน</strong></li>
                            <li>ผู้ป่วยติดบ้าน (Homebound) <strong>+2 คะแนน</strong></li>
                        </ul>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        * คะแนนรวมใช้เพื่อจัดลำดับความเร่งด่วน ไม่ใช่การวินิจฉัยทางการแพทย์
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenInfo(false)}>ปิด</Button>
                </DialogActions>
            </Dialog>

        </Paper>
    );
}

export default AssistancePriority;



